import { doc, getDoc } from "firebase/firestore";
import { UserPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { CommandFeedback } from "components/CommandFeedback";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { db } from "integrations/firebase/firestore";

import { useAdminCommand } from "../admin/use-admin-command";
import { userProfileSchema, type UserProfile } from "../profile/schemas";

import {
  ALREADY_ATTENDEE,
  addMeFailureMessage,
  selfAttendeePayload,
} from "./self-attendee";

/**
 * Whether the card belongs on this page: an event admin whose membership does
 * not point at an attendee row. Everybody else either registered (and has a
 * row) or cannot add attendees at all.
 */
export function canAddSelfAsAttendee(membership: {
  isEventAdmin?: boolean;
  attendeeId?: string;
}): boolean {
  return membership.isEventAdmin === true && !membership.attendeeId;
}

/** The person's own profile, or null when it cannot be read or parsed. */
async function readOwnProfile(uid: string): Promise<UserProfile | null> {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return null;
    const parsed = userProfileSchema.safeParse(snap.data());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * One tap for an organiser who created the event and is not on its attendee
 * list, which is what keeps them out of signals, the directory, seating and
 * networking. It files the ordinary ADD_EVENT_ATTENDEE command for the
 * organiser's own address; the backend links the row to their account, writes
 * the attendee id onto their membership and fills the row from their profile.
 *
 * `onAdded` runs once that command has succeeded, so the page can read the
 * membership again and drop this card. It does not run when the backend says
 * the address is already on the list: the read would unmount the page and
 * take the explanation below with it, and the row is already there, so the
 * membership has not moved on.
 *
 * Nothing is filed unless the person's own profile has been read. The backend
 * links a row to an account by the profile's address, and a row written
 * without that link still ends in success while leaving the organiser off the
 * list.
 */
export function AddMeAsAttendee({
  eventId,
  uid,
  email,
  onAdded,
}: {
  eventId: string;
  uid: string;
  email: string | null;
  onAdded?: () => void;
}) {
  const command = useAdminCommand();
  const [preparing, setPreparing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // A ref as well as state: the profile read happens before the command moves
  // to "submitting", and a second tap in that gap must not file a second one.
  const inFlight = useRef(false);

  const status = command.state.status;
  const busy = preparing || status === "submitting" || status === "watching";

  useEffect(() => {
    if (command.state.status === "succeeded") {
      inFlight.current = false;
      toast.success("You are on the attendee list");
      command.reset();
      onAdded?.();
    } else if (command.state.status === "failed") {
      inFlight.current = false;
      if (ALREADY_ATTENDEE.test(command.state.error)) {
        toast.error(addMeFailureMessage(command.state.error));
      }
    }
    // Keyed on the status alone: `command` is a new object every render.
  }, [command.state.status]);

  const add = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLocalError(null);
    setPreparing(true);
    const profile = await readOwnProfile(uid);
    const payload = profile ? selfAttendeePayload(profile, email) : null;
    setPreparing(false);
    if (!profile) {
      inFlight.current = false;
      setLocalError(
        "We could not read your profile. Check your connection and try again.",
      );
      return;
    }
    if (!payload) {
      inFlight.current = false;
      setLocalError(
        "We could not find your email address. Add it to your profile and try again.",
      );
      return;
    }
    await command.submit("ADD_EVENT_ATTENDEE", eventId, uid, payload);
  };

  const error =
    localError ??
    (command.state.status === "failed"
      ? addMeFailureMessage(command.state.error)
      : null);

  return (
    <Card data-testid="add-me-as-attendee">
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start gap-3">
          <UserPlus
            className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-foreground">
              You organise this event but are not an attendee
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Adding yourself puts you on the attendee list with your profile
              details. You can then pick your signals and appear in the
              directory.
            </p>
          </div>
        </div>
        {busy && (
          <CommandFeedback
            state={{ status: "pending" }}
            onReset={() => {}}
            pendingMessage="Adding you..."
            successMessage=""
            testId="add-me-feedback"
          />
        )}
        {error && !busy && (
          <CommandFeedback
            state={{ status: "failed", error }}
            onReset={() => {
              setLocalError(null);
              command.reset();
            }}
            pendingMessage=""
            successMessage=""
            testId="add-me-feedback"
          />
        )}
        <Button
          variant="cta"
          size="lg"
          className="w-full sm:w-auto"
          disabled={busy}
          aria-busy={busy}
          onClick={() => void add()}
          data-testid="add-me-as-attendee-button"
        >
          {error && !busy ? "Try again" : "Add me as attendee"}
        </Button>
      </CardContent>
    </Card>
  );
}
